import { useState, type ReactNode } from 'react'
import {
  Camera, Eye, Star, MousePointerClick, ImageIcon, Send, PlugZap, MapPin,
} from 'lucide-react'
import { Link } from '../router.js'
import { LiveNumber, QuietNumber } from '../soirees/lib.js'
import { useCommunication } from './data.js'
import {
  HEALTH_FR, PUBLICATION_STATE_FR, deltaClasse, deltaTexte, fraicheurFr,
  nombreFr, type GoogleOverview, type HonestDelta, type InstagramOverview,
} from './lib.js'

/**
 * WIDGETS DE LA HOME COMMUNICATION.
 *
 * Chaque tuile distingue ses états (§126) : chargement · non connecté ·
 * historique en construction · données · erreur. Un canal absent n'est pas
 * une panne — c'est une invitation à connecter (§127), jamais un « 0 »
 * inventé ni un « +0 % » mensonger.
 */

/* ─────────────────────────── briques communes ─────────────────────────── */

function CarteWidget({ tint, children }: { tint: string; children: ReactNode }) {
  return (
    <div className={`lc-card lc-card--${tint} cm-widget`}>
      <span className="lc-card__edge" aria-hidden="true" />
      {children}
    </div>
  )
}

/** État vide PREMIUM (§127) — dit ce que la tuile montrera, propose l'action. */
function EtatVide({ Icon, titre, detail, href, cta }: {
  Icon: typeof Camera; titre: string; detail: string; href: string; cta: string
}) {
  return (
    <div className="cm-empty">
      <span className="cm-empty__icon" aria-hidden="true"><Icon size={22} /></span>
      <strong>{titre}</strong>
      <p>{detail}</p>
      <Link href={href} className="lc-btn">{cta}</Link>
    </div>
  )
}

function Chargement() {
  return <p className="lc-hint cm-loading">Chargement…</p>
}

function ErreurLecture({ detail }: { detail: string }) {
  return (
    <div className="cm-empty">
      <span className="cm-empty__icon cm-empty__icon--warn" aria-hidden="true"><PlugZap size={22} /></span>
      <strong>Lecture impossible</strong>
      <p>{detail}</p>
    </div>
  )
}

/** Delta honnête : « +12 (+3 %) vs période précédente » ou l'attente réelle. */
function DeltaLine({ delta, vs }: { delta: HonestDelta | null; vs: string }) {
  const t = deltaTexte(delta)
  if (t) {
    return (
      <span className={`cm-delta cm-delta--${deltaClasse(delta)}`}>
        {t.abs}{t.pct ? ` (${t.pct})` : ''} <em>{vs}</em>
      </span>
    )
  }
  if (delta?.state === 'NO_HISTORY') {
    return (
      <span className="cm-delta cm-delta--none">
        Historique en construction
        {delta.availableInDays !== null ? ` — comparaison dans ${delta.availableInDays} j` : ''}
      </span>
    )
  }
  return null
}

/** Mini-courbe SVG — uniquement quand il y a AU MOINS deux points réels. */
function Sparkline({ points }: { points: { day: string; value: number }[] }) {
  if (points.length < 2) return null
  const vals = points.map((p) => p.value)
  const min = Math.min(...vals); const max = Math.max(...vals)
  const span = max - min || 1
  const W = 100; const H = 28
  const d = points.map((p, i) => {
    const x = (i / (points.length - 1)) * W
    const y = H - 3 - ((p.value - min) / span) * (H - 6)
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')
  return (
    <svg className="cm-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

/**
 * Vignette de contenu : les URLs CDN Instagram sont réelles mais la CSP de
 * Gestion (img-src) les bloque — décision de sécurité assumée, pas un bug.
 * Une image qui ne charge pas retombe sur le pictogramme, jamais sur une
 * icône cassée.
 */
export function VignetteContenu({ src, taille = 34 }: { src: string | null; taille?: number }) {
  const [cassee, setCassee] = useState(false)
  if (!src || cassee) {
    return <span className="cm-contentlist__ph" aria-hidden="true"><ImageIcon size={Math.round(taille * 0.44)} /></span>
  }
  return <img src={src} alt="" loading="lazy" onError={() => setCassee(true)} />
}

function TeteWidget({ Icon, titre, sub }: { Icon: typeof Camera; titre: string; sub?: ReactNode }) {
  return (
    <header className="cm-widget__head">
      <span className="lc-card__icon" aria-hidden="true"><Icon /></span>
      <div>
        <h3>{titre}</h3>
        {sub ? <span className="cm-widget__sub">{sub}</span> : null}
      </div>
    </header>
  )
}

/* ═══════════════════════ 1 · Audience Instagram ════════════════════════ */

export function InstagramAudienceWidget() {
  const { data, loading, erreur } = useCommunication()
  const ig = data?.instagram ?? null
  return (
    <CarteWidget tint="violet">
      <TeteWidget Icon={Camera} titre="Instagram — audience"
        sub={ig?.connected ? <>{ig.username ? `@${ig.username} · ` : ''}{fraicheurFr(ig.lastSyncAt)}</> : null} />
      {erreur ? <ErreurLecture detail={erreur} />
        : !data && loading ? <Chargement />
        : !ig?.connected ? (
          <EtatVide Icon={Camera} titre="Instagram n’est pas connecté"
            detail="Cette tuile montrera vos abonnés et leur évolution, relevés chaque jour par Gestion."
            href="/communication/integrations/instagram" cta="Connecter Instagram" />
        ) : ig.followers === null ? (
          <EtatVide Icon={Camera} titre="Premier relevé en attente"
            detail="Le compte est relié — la première synchronisation créera le point de départ de l’historique."
            href="/communication/integrations/instagram" cta="Voir le diagnostic" />
        ) : (
          <>
            <div className="cm-big">
              <strong className="cm-big__num"><LiveNumber value={ig.followers} format={nombreFr} /></strong>
              <span className="cm-big__label">abonnés</span>
            </div>
            <DeltaLine delta={ig.followersDelta} vs="sur la période" />
            {ig.owner_connected ? (
              <ul className="cm-actionlist">
                {ig.reach_week?.state === 'OK' ? (
                  <li><span className="cm-actionlist__label">Reach cette semaine</span>
                    <strong>{nombreFr(ig.reach_week.current)}</strong>
                    {ig.reach_week.pct !== null ? <span className={`cm-delta cm-delta--${deltaClasse(ig.reach_week)}`}>{ig.reach_week.pct >= 0 ? '+' : ''}{ig.reach_week.pct.toLocaleString('fr-FR')} %</span> : null}</li>
                ) : null}
                {ig.interactions_week?.state === 'OK' ? (
                  <li><span className="cm-actionlist__label">Interactions</span>
                    <strong>{nombreFr(ig.interactions_week.current)}</strong></li>
                ) : null}
              </ul>
            ) : (
              // §74 — lecture publique limitée : dire clairement ce que la
              // connexion propriétaire débloque.
              <p className="lc-hint">
                Lecture publique limitée — connectez @{ig.username ?? 'le compte'} pour
                débloquer reach, vues, interactions et contenus détaillés.
              </p>
            )}
            <Sparkline points={ig.series} />
            <Link href={ig.owner_connected ? '/communication/instagram' : '/communication/integrations/instagram'} className="lc-btn cm-widget__cta">
              {ig.owner_connected ? 'Ouvrir Instagram' : 'Connecter Instagram'}
            </Link>
          </>
        )}
    </CarteWidget>
  )
}

/* ═══════════════════════ 2 · Visibilité Google ═════════════════════════ */

export function GoogleVisibilityWidget() {
  const { data, loading, erreur } = useCommunication()
  const g = data?.google ?? null
  return (
    <CarteWidget tint="cyan">
      <TeteWidget Icon={Eye} titre="Google — visibilité"
        sub={g?.connected ? <>{g.locationTitle ?? 'Fiche établissement'} · {fraicheurFr(g.lastSyncAt)}</> : null} />
      {erreur ? <ErreurLecture detail={erreur} />
        : !data && loading ? <Chargement />
        : !g?.connected ? (
          <EtatVide Icon={MapPin} titre="Google Business n’est pas connecté"
            detail="Cette tuile montrera combien de fois la fiche du club apparaît sur Maps et dans la recherche."
            href="/communication/integrations/google" cta="Connecter Google" />
        ) : g.impressions === null || (g.impressions.state === 'NO_HISTORY' && g.impressions.current === 0) ? (
          <EtatVide Icon={Eye} titre="Pas encore de mesures"
            detail="La fiche est reliée — les impressions Maps et Recherche arriveront avec les prochaines synchronisations (Google publie ces chiffres avec quelques jours de décalage)."
            href="/communication/integrations/google" cta="Voir le diagnostic" />
        ) : (
          <>
            <div className="cm-big">
              <strong className="cm-big__num"><LiveNumber value={g.impressions.current} format={nombreFr} /></strong>
              <span className="cm-big__label">impressions</span>
            </div>
            <DeltaLine delta={g.impressions} vs="vs période comparable" />
            <div className="cm-splitline">
              {g.maps !== null ? <span><Eye size={13} aria-hidden="true" /> Maps : {nombreFr(g.maps)}</span> : null}
              {g.search !== null ? <span>Recherche : {nombreFr(g.search)}</span> : null}
            </div>
            <Sparkline points={g.series} />
          </>
        )}
    </CarteWidget>
  )
}

/* ═══════════════════════ 3 · Actions Google ════════════════════════════ */

export function GoogleActionsWidget() {
  const { data, loading, erreur } = useCommunication()
  const g = data?.google ?? null
  const actions = g?.actions ?? []
  const sansMesure = actions.every((a) => a.delta.state === 'NO_HISTORY' && a.delta.current === 0)
  return (
    <CarteWidget tint="cyan">
      <TeteWidget Icon={MousePointerClick} titre="Google — actions clients" />
      {erreur ? <ErreurLecture detail={erreur} />
        : !data && loading ? <Chargement />
        : !g?.connected ? (
          <EtatVide Icon={MousePointerClick} titre="Google Business n’est pas connecté"
            detail="Itinéraires, appels et clics vers le site — les gestes concrets des clients depuis la fiche."
            href="/communication/integrations/google" cta="Connecter Google" />
        ) : sansMesure ? (
          <EtatVide Icon={MousePointerClick} titre="Pas encore de mesures"
            detail="Aucune action mesurée sur la période — Google publie ces chiffres avec quelques jours de décalage."
            href="/communication/integrations/google" cta="Voir le diagnostic" />
        ) : (
          <ul className="cm-actionlist">
            {actions.map((a) => {
              const t = deltaTexte(a.delta)
              return (
                <li key={a.key}>
                  <span className="cm-actionlist__label">{a.label}</span>
                  <strong><QuietNumber value={a.delta.current} /></strong>
                  {t?.pct ? <span className={`cm-delta cm-delta--${deltaClasse(a.delta)}`}>{t.pct}</span>
                    : <span className="cm-delta cm-delta--none">{a.delta.state === 'NO_HISTORY' ? 'sans comparaison' : ''}</span>}
                </li>
              )
            })}
          </ul>
        )}
    </CarteWidget>
  )
}

/* ═══════════════════ 4 · Contenus Instagram récents ════════════════════ */

export function InstagramTopContentWidget() {
  const { data, loading, erreur } = useCommunication()
  const ig = data?.instagram ?? null
  return (
    <CarteWidget tint="violet">
      <TeteWidget Icon={ImageIcon} titre="Instagram — contenus qui marchent" />
      {erreur ? <ErreurLecture detail={erreur} />
        : !data && loading ? <Chargement />
        : !ig?.connected ? (
          <EtatVide Icon={ImageIcon} titre="Instagram n’est pas connecté"
            detail="Cette tuile classera vos posts et Reels récents par interactions réelles (likes + commentaires)."
            href="/communication/integrations/instagram" cta="Connecter Instagram" />
        ) : ig.topContent.length === 0 ? (
          <EtatVide Icon={ImageIcon} titre="Aucun contenu collecté"
            detail="La prochaine synchronisation ramènera les publications récentes du compte."
            href="/communication/integrations/instagram" cta="Voir le diagnostic" />
        ) : (
          <ul className="cm-contentlist">
            {ig.topContent.slice(0, 4).map((c) => (
              <li key={c.id}>
                <Link href={`/communication/instagram/contenus/${c.id}`} className="cm-contentlist__row">
                  <VignetteContenu src={c.preview_asset_id ? `/api/v1/communication/instagram/media/${c.preview_asset_id}/file` : null} />
                  <span className="cm-contentlist__cap">{c.caption ? c.caption.slice(0, 64) : (c.kind ?? 'Publication')}</span>
                  <span className="cm-contentlist__stats">
                    {c.like_count !== null ? `${nombreFr(c.like_count)} ♥` : '♥ non exposés'}
                    {c.comments_count !== null ? ` · ${nombreFr(c.comments_count)} comm.` : ''}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
    </CarteWidget>
  )
}

/* ═══════════════════════ 5 · Avis Google ═══════════════════════════════ */

export function GoogleReviewsWidget() {
  const { data, loading, erreur } = useCommunication()
  const g = data?.google ?? null
  const r = g?.reviews ?? null
  return (
    <CarteWidget tint="amber">
      <TeteWidget Icon={Star} titre="Avis Google" />
      {erreur ? <ErreurLecture detail={erreur} />
        : !data && loading ? <Chargement />
        : !g?.connected && (r?.total ?? 0) === 0 ? (
          <EtatVide Icon={Star} titre="Aucun avis collecté"
            detail="Une fois Google Business connecté, la note officielle et les avis arrivent ici — avec les réponses à traiter."
            href="/communication/integrations/google" cta="Connecter Google" />
        ) : (
          <>
            <div className="cm-big">
              <strong className="cm-big__num">{r?.average_rating !== null && r ? <LiveNumber value={r.average_rating} format={(n) => n.toLocaleString('fr-FR')} /> : '—'}</strong>
              <span className="cm-big__label">/ 5 · {nombreFr(r?.total ?? 0)} avis</span>
            </div>
            <div className="cm-splitline">
              <span>{r?.new_this_week ?? 0} cette semaine</span>
              {r && r.to_answer > 0
                ? <span className="cm-badge-attn">{r.to_answer} sans réponse</span>
                : <span>tout est répondu</span>}
            </div>
            <Link href="/communication/avis" className="lc-btn cm-widget__cta">Ouvrir les avis</Link>
          </>
        )}
    </CarteWidget>
  )
}

/* ═══════════════════ 6 · Publications à venir ══════════════════════════ */

export function PublicationsUpcomingWidget() {
  const { data, loading, erreur } = useCommunication()
  const p = data?.publications ?? null
  const total = p ? p.scheduled + p.awaiting + p.drafts : 0
  return (
    <CarteWidget tint="violet">
      <TeteWidget Icon={Send} titre="Publications" />
      {erreur ? <ErreurLecture detail={erreur} />
        : !data && loading ? <Chargement />
        : total === 0 ? (
          <EtatVide Icon={Send} titre="Rien en préparation"
            detail="Préparez un post multi-canaux — au besoin depuis une soirée, avec ses vraies informations."
            href="/communication/publications/nouvelle" cta="Nouvelle publication" />
        ) : (
          <>
            <ul className="cm-actionlist">
              {p!.scheduled > 0 ? <li><span className="cm-actionlist__label">Programmées</span><strong>{p!.scheduled}</strong></li> : null}
              {p!.awaiting > 0 ? (
                <li>
                  <span className="cm-actionlist__label">{PUBLICATION_STATE_FR.AWAITING_WRITE_ACCESS}</span>
                  <strong>{p!.awaiting}</strong>
                </li>
              ) : null}
              {p!.drafts > 0 ? <li><span className="cm-actionlist__label">Brouillons</span><strong>{p!.drafts}</strong></li> : null}
            </ul>
            <Link href="/communication/publications" className="lc-btn cm-widget__cta">Ouvrir les publications</Link>
          </>
        )}
    </CarteWidget>
  )
}

/* ═══════════════════ 7 · Santé des intégrations ════════════════════════ */

function pillPourSante(h: string) {
  if (h === 'OPERATIONAL') return <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok lc-dot--live" />{HEALTH_FR[h]}</span>
  if (h === 'NOT_CONFIGURED') return <span className="lc-pill lc-pill--idle">{HEALTH_FR[h]}</span>
  return <span className="lc-pill lc-pill--warning">{HEALTH_FR[h] ?? h}</span>
}

export function IntegrationsHealthWidget() {
  const { data, loading, erreur } = useCommunication()
  return (
    <CarteWidget tint="neutral">
      <TeteWidget Icon={PlugZap} titre="Canaux connectés" />
      {erreur ? <ErreurLecture detail={erreur} />
        : !data && loading ? <Chargement />
        : (
          <>
            <ul className="cm-healthlist">
              <li>
                <span>Instagram{data!.instagram.username ? ` · @${data!.instagram.username}` : ''}</span>
                {pillPourSante(data!.instagram.health)}
              </li>
              <li>
                <span>Google Business{data!.google.locationTitle ? ` · ${data!.google.locationTitle}` : ''}</span>
                {pillPourSante(data!.google.health)}
              </li>
            </ul>
            <Link href="/communication/integrations" className="lc-btn cm-widget__cta">Gérer les intégrations</Link>
          </>
        )}
    </CarteWidget>
  )
}

export type { InstagramOverview, GoogleOverview }
